/**
 * Canonical Sales Pipeline deal-stage machine values.
 * Display labels: GET /api/platform/constants → options.sales_pipeline.deal_stage
 */
import {
  DEFAULT_DOMAIN_OPTIONS,
  labelMapFor,
  type DealStageId,
} from "@/constants";

export type { DealStageId };

export const DEAL_STAGE = {
  REACH_OUT: "reach_out",
  OPPORTUNITY: "opportunity",
  QUALIFIED: "qualified",
  DESIGNING: "designing",
  NEGOTIATION: "negotiation",
  WON: "won",
  LOST: "lost",
} as const satisfies Record<string, DealStageId>;

export const DEAL_STAGE_LABEL = labelMapFor<DealStageId>(
  DEFAULT_DOMAIN_OPTIONS,
  "sales_pipeline",
  "deal_stage",
);

/** Active pipeline columns (excludes Won / Lost). Reach Outs is always first. */
export const PIPELINE_STAGES = [
  DEAL_STAGE.REACH_OUT,
  DEAL_STAGE.OPPORTUNITY,
  DEAL_STAGE.QUALIFIED,
  DEAL_STAGE.DESIGNING,
  DEAL_STAGE.NEGOTIATION,
] as const;

export type PipelineStageId = (typeof PIPELINE_STAGES)[number];

export const TERMINAL_STAGES = [DEAL_STAGE.WON, DEAL_STAGE.LOST] as const;

export type TerminalStageId = (typeof TERMINAL_STAGES)[number];

export const ALL_DEAL_STAGES = [...PIPELINE_STAGES, ...TERMINAL_STAGES] as const;

/** Next stage when promoting a deal. */
export const STAGE_PROGRESSION: Record<PipelineStageId, DealStageId> = {
  [DEAL_STAGE.REACH_OUT]: DEAL_STAGE.OPPORTUNITY,
  [DEAL_STAGE.OPPORTUNITY]: DEAL_STAGE.QUALIFIED,
  [DEAL_STAGE.QUALIFIED]: DEAL_STAGE.DESIGNING,
  [DEAL_STAGE.DESIGNING]: DEAL_STAGE.NEGOTIATION,
  [DEAL_STAGE.NEGOTIATION]: DEAL_STAGE.WON,
};

export const STAGE_PROMOTION_LABELS: Record<PipelineStageId, string> = {
  [DEAL_STAGE.REACH_OUT]: "Convert",
  [DEAL_STAGE.OPPORTUNITY]: "Qualify Lead",
  [DEAL_STAGE.QUALIFIED]: "Start Design",
  [DEAL_STAGE.DESIGNING]: "Request Quote",
  [DEAL_STAGE.NEGOTIATION]: "Mark Won",
};

/**
 * Legacy / alias keys → canonical id.
 * Use only at API/DB boundaries until backend enums are migrated.
 */
export const LEGACY_DEAL_STAGE_ALIASES: Record<string, DealStageId> = {
  reach_out: DEAL_STAGE.REACH_OUT,
  reach_outs: DEAL_STAGE.REACH_OUT,
  reachout: DEAL_STAGE.REACH_OUT,

  opportunity: DEAL_STAGE.OPPORTUNITY,
  opportunities: DEAL_STAGE.OPPORTUNITY,
  new_lead: DEAL_STAGE.OPPORTUNITY,
  opp: DEAL_STAGE.OPPORTUNITY,

  qualified: DEAL_STAGE.QUALIFIED,
  leads: DEAL_STAGE.QUALIFIED,
  lead: DEAL_STAGE.QUALIFIED,
  warm_lead: DEAL_STAGE.QUALIFIED,
  hot_lead: DEAL_STAGE.QUALIFIED,
  active_lead: DEAL_STAGE.QUALIFIED,

  designing: DEAL_STAGE.DESIGNING,
  "proof-submitted": DEAL_STAGE.DESIGNING,
  proof_submitted: DEAL_STAGE.DESIGNING,
  proof: DEAL_STAGE.DESIGNING,

  negotiation: DEAL_STAGE.NEGOTIATION,
  negotiating: DEAL_STAGE.NEGOTIATION,
  quoted: DEAL_STAGE.NEGOTIATION,
  neg: DEAL_STAGE.NEGOTIATION,

  won: DEAL_STAGE.WON,
  lost: DEAL_STAGE.LOST,
};

export function resolveDealStageId(key: string): DealStageId | null {
  return LEGACY_DEAL_STAGE_ALIASES[key] ?? null;
}

export function isDealStageId(key: string): key is DealStageId {
  return (ALL_DEAL_STAGES as readonly string[]).includes(key);
}

export function getDealStageLabel(key: string): string {
  const id = resolveDealStageId(key);
  return id ? DEAL_STAGE_LABEL[id] : key;
}

export const DEAL_STAGE_META: ReadonlyArray<{ id: DealStageId; label: string }> =
  ALL_DEAL_STAGES.map((id) => ({ id, label: DEAL_STAGE_LABEL[id] }));
